import { getBobLevel } from '../../../packages/domain/src/67-bob-levels.mjs';
import { BOB_PHYSICS, bobHazardsAt } from '../../../packages/domain/src/68-bob-bounce.mjs';

const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const contacts = new Set([
  'bounce',
  'launch',
  'land',
  'ground',
  'platform',
  'bumper',
  'timing',
  'stumble',
  'spring',
  'crate',
]);

/** Render-only anticipation/recoil and fixed-step interpolation. Accepted event
 * IDs trigger finite motion; this owner never applies velocity, collision, points
 * or input. dt is visible seconds from the controller's one clock. */
export function createBobMotion({ bob, groundShadow }) {
  let previousBob, currentBob;
  let sampleTime = -1,
    serial = 0,
    clock = 0;
  let happyUntil = 0,
    fearBlend = 0,
    fearUntil = 0;
  let finishTurnSeconds = 0;
  let impact = { age: 10, strength: 0, normal: { x: 0, y: 1, z: 0 } };
  function update(state, dt, reduced) {
    if (!currentBob || state.elapsed < sampleTime || state.eventSerial < serial) {
      previousBob = currentBob = { ...state.bob };
      serial = 0;
      happyUntil = 0;
      fearBlend = 0;
      fearUntil = 0;
      finishTurnSeconds = 0;
      impact = { age: 10, strength: 0, normal: { x: 0, y: 1, z: 0 } };
    } else if (state.elapsed !== sampleTime || state.status !== 'playing') {
      previousBob = currentBob;
      currentBob = { ...state.bob };
    }
    sampleTime = state.elapsed;
    clock += dt;
    finishTurnSeconds =
      state.status === 'complete' ? (reduced ? 0.5 : Math.min(0.5, finishTurnSeconds + dt)) : 0;
    impact.age += dt;
    for (const event of state.events || []) {
      if (event.id <= serial) continue;
      serial = event.id;
      if (event.type === 'stumble') fearUntil = clock + 0.9;
      if (['food', 'powerup', 'crate', 'bonus-route'].includes(event.type))
        happyUntil = clock + 0.85;
      if (contacts.has(event.type))
        impact = {
          age: 0,
          strength: event.grade === 'PERFECT' ? 1.2 : event.type === 'bumper' ? 1.05 : 0.85,
          normal: event.normal || { x: 0, y: 1, z: 0 },
        };
    }
    const alpha =
      reduced || !dt || state.status !== 'playing'
        ? 1
        : Math.min(1, (state.accumulator || 0) / BOB_PHYSICS.stepMs);
    const b = { ...state.bob };
    for (const axis of ['x', 'y', 'z'])
      b[axis] = previousBob[axis] + (currentBob[axis] - previousBob[axis]) * alpha;
    bob.root.position.set(b.x, b.y, b.z);
    // The model's nose points down local -Z. Course progress is world +Z:
    // turn the whole rig toward travel, with a bounded sideways heading.
    const heading = Math.PI + clamp(Math.atan2(b.vx, Math.max(1, b.vz)), -0.5, 0.5);
    const turn = finishTurnSeconds / 0.5;
    // The finite result pose turns the authored local -Z face back toward the
    // trailing camera. Root coordinates and gameplay heading stay untouched.
    bob.root.rotation.y = heading * (1 - turn * turn * (3 - 2 * turn));
    groundShadow.position.set(b.x, 0.12, b.z);
    groundShadow.scale.setScalar(0.42 + Math.max(0, b.y) * 0.035);
    groundShadow.material.opacity = Math.max(0.1, 0.36 / (1 + Math.max(0, b.y) * 0.24));
    const pulse = reduced ? 0 : impact.strength * Math.exp(-impact.age * 11);
    const recoil = reduced
      ? 0
      : impact.strength * Math.sin(impact.age * 22) * Math.exp(-impact.age * 7);
    const gap = Math.max(0, b.y - BOB_PHYSICS.radius);
    const anticipation = reduced || b.vy >= -0.5 ? 0 : clamp(1 - gap / 1.1, 0, 1);
    const stretch = reduced
      ? 0
      : clamp(Math.abs(b.vy) * 0.014, 0, 0.14) - anticipation * 0.1 - pulse * 0.25 + recoil * 0.1;
    bob.body.scale.set(1 - stretch * 0.45, 1 + stretch, 1 - stretch * 0.45);
    bob.body.rotation.x = reduced
      ? 0
      : clamp(b.vz * 0.012, -0.14, 0.14) + impact.normal.z * pulse * 0.18;
    bob.root.rotation.z = reduced
      ? 0
      : clamp(-b.vx * 0.022, -0.2, 0.2) + impact.normal.x * pulse * 0.22;
    bob.head.rotation.y = reduced ? 0 : clamp(-b.vx * 0.035, -0.24, 0.24);
    bob.head.rotation.z = -bob.root.rotation.z * 0.42;
    bob.head.position.y = 0.61 + recoil * 0.065;
    bob.cap.position.y = reduced ? 0 : Math.max(0, recoil * 0.1);
    bob.cap.rotation.z = reduced ? 0 : Math.sin(impact.age * 16) * pulse * 0.12;
    bob.arms.forEach((arm, i) => {
      arm.rotation.z =
        (i ? -1 : 1) * (0.15 + Math.max(0, b.vy) * 0.06 + anticipation * 0.24 + pulse * 0.45);
      arm.rotation.x = reduced
        ? 0
        : Math.sin(clock * 9 + i * Math.PI) * Math.min(0.3, Math.abs(b.vz) * 0.025) +
          recoil * (i ? -0.35 : 0.35);
    });
    bob.legs.forEach((leg, i) => {
      leg.rotation.x = reduced
        ? 0
        : Math.sin(clock * 9 + i * Math.PI) * Math.min(0.4, Math.abs(b.vz) * 0.035) -
          anticipation * 0.26;
      leg.rotation.z = reduced ? 0 : (i ? -1 : 1) * pulse * 0.2;
    });
    const course = getBobLevel(state.level);
    const nearEdge = b.x < course.bounds.minX + 0.7 || b.x > course.bounds.maxX - 0.7;
    const nearGround = b.vy < -1 && gap < 1.1;
    const nearHazard = bobHazardsAt(state.level, state.elapsed).some(
      (h) =>
        h.behavior === 'hazard' &&
        Math.abs(b.x - h.x) < h.width / 2 + 1 &&
        Math.abs(b.y - h.y) < h.height / 2 + 1 &&
        Math.abs(b.z - h.z) < h.depth / 2 + 1,
    );
    const fear =
      state.status === 'failed' ||
      clock < fearUntil ||
      (state.status === 'playing' && (nearEdge || nearGround || nearHazard))
        ? 1
        : 0;
    fearBlend += (fear - fearBlend) * (reduced || !dt ? 1 : 1 - Math.exp(-dt * 14));
    const happy = fearBlend > 0.45 ? 0 : clamp((happyUntil - clock) / 0.45, 0, 1);
    const eyeHeight = reduced
      ? 1 + fear * 0.2
      : clamp(1 - pulse * 0.85 + anticipation * 0.18 + fearBlend * 0.3 + happy * 0.15, 0.18, 1.3);
    bob.eyes.forEach((eye) => {
      eye.scale.y = eyeHeight;
    });
    bob.brows.forEach((brow, i) => {
      brow.rotation.z = (i ? 1 : -1) * (0.1 + anticipation * 0.12 - fearBlend * 0.22);
      brow.position.y = 0.162 + fearBlend * 0.034 + happy * 0.018;
    });
    bob.head.rotation.x = fearBlend * 0.13 - happy * 0.08;
    bob.mouthOpen.visible = fearBlend > 0.45;
    bob.smile.visible = !bob.mouthOpen.visible;
    bob.smile.rotation.z = 0;
    bob.smile.scale.set(0.105 + happy * 0.03, 0.063 + happy * 0.012, 0.105);
    for (let i = 0; i < bob.arms.length; i++)
      bob.arms[i].rotation.z += (i ? -1 : 1) * (fearBlend * 0.38 + happy * 0.5);
    return b;
  }
  return { update };
}
